class GlovesMainProduct {
  constructor(section) {
    this.section = section;
    this.moneyFormat = section.dataset.moneyFormat || '${{amount}}';
    this.variants = this.readVariants();
    this.variantInput = section.querySelector('[data-gloves-variant-id]');
    this.quantity = section.querySelector('[data-gloves-quantity]');
    this.submit = section.querySelector('[data-gloves-submit]');
    this.addLabel = section.querySelector('[data-gloves-add-label]');
    this.price = section.querySelector('[data-gloves-price]');
    this.shipping = section.querySelector('[data-gloves-shipping-status]');
    this.purchaseOptions = [...section.querySelectorAll('[data-gloves-purchase-option]')];
    this.currentVariant = this.variants.find((variant) => String(variant.id) === this.variantInput?.value) || this.variants[0];

    this.initGallery();
    this.initOptions();
    this.initPurchaseOptions();
    this.initQuantity();
    this.selectVariant(this.currentVariant, false);
  }

  readVariants() {
    try {
      return JSON.parse(this.section.querySelector('[data-gloves-variants]')?.textContent || '[]');
    } catch (error) {
      return [];
    }
  }

  initGallery() {
    const panels = [...this.section.querySelectorAll('.belt-product__media-panel')];
    this.section.querySelectorAll('[data-gloves-media-target]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        panels.forEach((panel) => {
          const active = panel.id === thumb.dataset.glovesMediaTarget;
          panel.hidden = !active;
          if (!active) panel.querySelector('video')?.pause();
        });
        this.section.querySelectorAll('[data-gloves-media-target]').forEach((item) => {
          item.classList.toggle('is-active', item === thumb);
        });
        panels.find((panel) => panel.id === thumb.dataset.glovesMediaTarget)?.querySelector('video')?.play().catch(() => {});
      });
    });
  }

  initOptions() {
    this.section.querySelectorAll('[data-option-position]').forEach((group) => {
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        button.addEventListener('click', () => {
          group.querySelectorAll('[data-option-value]').forEach((item) => {
            item.classList.toggle('is-active', item === button);
            item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
          });
          const selected = group.querySelector('[data-selected-option]');
          if (selected) selected.textContent = button.dataset.optionValue;

          const variant = this.findSelectedVariant();
          this.selectVariant(variant, true);
        });
      });
    });
  }

  selectedOptions() {
    return [...this.section.querySelectorAll('[data-option-position]')]
      .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
      .map((group) => group.querySelector('[data-option-value].is-active')?.dataset.optionValue || '');
  }

  findSelectedVariant() {
    const selected = this.selectedOptions();
    return this.variants.find((variant) => variant.options.every((option, index) => option === selected[index]));
  }

  selectVariant(variant, updateUrl) {
    this.currentVariant = variant || null;
    if (this.variantInput) {
      this.variantInput.value = variant?.id || '';
      this.variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (variant && updateUrl) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    this.updateOptionAvailability();
    this.updatePrice();
    this.updatePurchase();
  }

  updateOptionAvailability() {
    const selected = this.selectedOptions();
    this.section.querySelectorAll('[data-option-position]').forEach((group) => {
      const position = Number(group.dataset.optionPosition) - 1;
      group.querySelectorAll('[data-option-value]').forEach((button) => {
        const possible = this.variants.some((variant) => variant.available && variant.options.every((option, index) => {
          if (index === position) return option === button.dataset.optionValue;
          return !selected[index] || option === selected[index];
        }));
        button.classList.toggle('is-unavailable', !possible);
        button.setAttribute('aria-disabled', possible ? 'false' : 'true');
      });
    });
  }

  updatePrice() {
    if (!this.price || !this.currentVariant) return;
    const compare = Number(this.currentVariant.compare_at_price || 0);
    const current = Number(this.currentVariant.price || 0);
    this.price.innerHTML = `<span>${this.formatMoney(current)}</span>${compare > current ? `<s>${this.formatMoney(compare)}</s>` : ''}`;
  }

  initQuantity() {
    this.section.querySelector('[data-gloves-quantity-minus]')?.addEventListener('click', () => this.setQuantity(this.value() - 1));
    this.section.querySelector('[data-gloves-quantity-plus]')?.addEventListener('click', () => this.setQuantity(this.value() + 1));
    this.quantity?.addEventListener('change', () => this.setQuantity(this.value()));
  }

  initPurchaseOptions() {
    this.purchaseOptions.forEach((option) => {
      option.addEventListener('click', () => {
        this.purchaseOptions.forEach((item) => {
          const active = item === option;
          item.classList.toggle('is-active', active);
          item.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        this.setQuantity(Number(option.dataset.quantity || 1));
      });
    });
  }

  activePurchaseOption() {
    return this.purchaseOptions.find((option) => option.classList.contains('is-active')) || null;
  }

  purchaseDiscount() {
    return Number(this.activePurchaseOption()?.dataset.discount || 0);
  }

  purchaseTotal() {
    if (!this.currentVariant) return 0;
    const regularTotal = Number(this.currentVariant.price || 0) * this.value();
    return Math.round(regularTotal * (100 - this.purchaseDiscount()) / 100);
  }

  updatePurchaseOptionPrices() {
    if (!this.currentVariant) return;
    this.purchaseOptions.forEach((option) => {
      const quantity = Number(option.dataset.quantity || 1);
      const discount = Number(option.dataset.discount || 0);
      const price = Math.round(Number(this.currentVariant.price || 0) * quantity * (100 - discount) / 100);
      const priceNode = option.querySelector('[data-gloves-option-price]');
      if (priceNode) priceNode.textContent = this.formatMoney(price);
    });
  }

  value() {
    return Math.max(1, Math.min(99, Number(this.quantity?.value || 1)));
  }

  setQuantity(value) {
    if (this.quantity) this.quantity.value = Math.max(1, Math.min(99, Number(value) || 1));
    this.updatePurchase();
  }

  updatePurchase() {
    const available = Boolean(this.currentVariant?.available);
    this.updatePurchaseOptionPrices();
    if (this.submit) this.submit.disabled = !available;
    if (this.addLabel) {
      this.addLabel.textContent = available
        ? `${this.submit?.dataset.addLabel || 'Add to cart'} · ${this.formatMoney(this.purchaseTotal())}`
        : this.currentVariant ? 'Sold out' : 'Unavailable';
    }
    this.updateShipping(available ? this.purchaseTotal() : 0);
  }

  updateShipping(total) {
    if (!this.shipping) return;
    const threshold = Math.max(0, Number(this.shipping.dataset.freeShippingThreshold || 0));
    const remaining = Math.max(0, threshold - total);
    const unlocked = threshold === 0 || remaining === 0;
    const message = this.shipping.querySelector('[data-gloves-shipping-message]');
    const progress = this.shipping.querySelector('[data-gloves-shipping-progress]');
    this.shipping.classList.toggle('is-unlocked', unlocked);
    const lockedText = this.shipping.dataset.lockedText || "You're [amount] from free shipping";
    const unlockedText = this.shipping.dataset.unlockedText || "You've unlocked free shipping";
    if (message) message.textContent = unlocked ? unlockedText : lockedText.replace('[amount]', this.formatMoney(remaining));
    if (progress) {
      progress.hidden = unlocked;
      const bar = progress.querySelector('i');
      if (bar) bar.style.width = `${threshold ? Math.min(100, total / threshold * 100) : 100}%`;
    }
    const start = this.addBusinessDays(new Date(), Number(this.shipping.dataset.businessDays || 5));
    const end = this.addBusinessDays(start, Number(this.shipping.dataset.deliveryWindow || 1));
    const delivery = this.shipping.querySelector('[data-gloves-delivery-message]');
    if (delivery) delivery.innerHTML = `Ships <b>within ${Number(this.shipping.dataset.shippingHours || 24)} hours</b>. Estimated arrival <b>${this.dateLabel(start)}</b> to <b>${this.dateLabel(end)}</b>.`;
  }

  addBusinessDays(date, days) {
    const result = new Date(date);
    let added = 0;
    while (added < days) {
      result.setDate(result.getDate() + 1);
      if (result.getDay() !== 0 && result.getDay() !== 6) added += 1;
    }
    return result;
  }

  dateLabel(date) {
    return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' }).format(date);
  }

  formatMoney(cents) {
    if (window.Shopify?.formatMoney) return window.Shopify.formatMoney(cents, this.moneyFormat);
    const value = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cents / 100);
    return this.moneyFormat.replace(/\{\{\s*\w+\s*\}\}/, value);
  }
}

const initGlovesMainProducts = (scope = document) => {
  scope.querySelectorAll('[data-gloves-main-product]').forEach((section) => {
    if (!section.glovesMainProduct) section.glovesMainProduct = new GlovesMainProduct(section);
  });
};

document.addEventListener('DOMContentLoaded', () => initGlovesMainProducts());
document.addEventListener('shopify:section:load', (event) => initGlovesMainProducts(event.target));
